import { useEffect, useRef, type ReactNode } from "react";

/** Open dialogs, oldest first. Only the last one reacts to Escape. */
const stack: symbol[] = [];

/** A modal dialog (QDialog): backdrop, Esc to close, focus moves in and returns on close. */
export function Dialog({ title, onClose, children, width = 420, testId }: {
  title: string; onClose: () => void; children: ReactNode; width?: number; testId?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>("input, select, textarea, button")?.focus();
    const me = Symbol("dialog");
    stack.push(me);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && stack[stack.length - 1] === me) { e.stopPropagation(); onClose(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      stack.splice(stack.indexOf(me), 1);
      prev?.focus?.();
    };
  }, [onClose]);
  return (
    <div className="backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title} data-testid={testId} style={{ width }} ref={ref}>
        <div className="dialog-title" style={{ marginBottom: 12 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

/** QMessageBox.information / .question with one or two buttons. */
export function MessageBox({ title, text, onClose, confirm, confirmLabel = "OK", danger }: {
  title: string; text: ReactNode; onClose: () => void; confirm?: () => void; confirmLabel?: string; danger?: boolean;
}) {
  return (
    <Dialog title={title} onClose={onClose} testId="messagebox">
      <div style={{ whiteSpace: "pre-wrap", marginBottom: 8 }}>{text}</div>
      <div className="dialog-actions">
        {confirm && <button className="btn" onClick={onClose}>Cancel</button>}
        <button className={`btn ${danger ? "danger" : "primary"}`} onClick={() => { confirm?.(); onClose(); }}>{confirm ? confirmLabel : "OK"}</button>
      </div>
    </Dialog>
  );
}
